// 🚦 베타 기능 토글 클라이언트 (2026-08-22 — 서버 routes/flags.ts 짝) — 새 기능을 단계 공개할 때
//   `useFeatureFlag('이름')` 으로 감싼다. 플래그 미정의·미로그인·조회 실패 = 꺼짐(안전 기본값).
//   5분 캐시 — 관리자가 켜면 각 사용자 다음 동기화(≤5분) 또는 새로고침부터 보인다.
import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';

let cache: Record<string, boolean> = {};
let fetchedAt = 0;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();
const TTL_MS = 5 * 60_000;

export async function syncFeatureFlags(force = false): Promise<void> {
	if (!force && Date.now() - fetchedAt < TTL_MS) return;
	if (inflight) return inflight;
	// 🔴 로그인 확인은 inflight 를 만들기 **전에** (2026-10-26 v1.0.79 실사고) — 종전엔 IIFE 안에서 미로그인이면 바로 return 했는데,
	//   첫 await 전에 끝나 finally(inflight=null)가 먼저 돌고 그 뒤 바깥 대입이 끝난 promise 를 inflight 에 남겼다 →
	//   이후 모든 동기화가 그 promise 를 돌려받아 영영 조회하지 않음. 로그인 전 화면에서 훅이 처음 불린 적이 없어 숨어 있다가,
	//   App 레벨 MeetRecHost(로그인 화면에서도 마운트)가 생기며 '새로 로그인한 사람은 기능 스위치 전부 꺼짐'으로 터졌다.
	const { authToken, userId } = useAuthStore.getState();
	if (!authToken || !userId) return;
	inflight = (async () => {
		try {
			const r = await fetch('/api/tpt/flags', { headers: { 'X-Auth-Token': authToken, 'X-User-Id': userId } });
			if (!r.ok) return;
			const j = await r.json().catch(() => ({})) as { flags?: Record<string, boolean> };
			cache = j.flags || {};
			fetchedAt = Date.now();
			listeners.forEach((fn) => { try { fn(); } catch { /* */ } });
		} catch { /* 실패 = 기존 캐시 유지(기본 꺼짐) */ } finally { inflight = null; }
	})();
	return inflight;
}

/** 동기 조회 — effect/핸들러용. 아직 동기화 전이면 false. */
export function isFeatureOn(name: string): boolean { return !!cache[name]; }

/** 리액티브 훅 — 마운트 시 동기화(5분 캐시)하고, 갱신되면 재렌더. */
export function useFeatureFlag(name: string): boolean {
	const [, tick] = useState(0);
	const authToken = useAuthStore((s) => s.authToken);   // 로그인 전에 마운트된 훅(App 레벨)도 로그인 순간 다시 동기화
	useEffect(() => {
		const fn = () => tick((n) => n + 1);
		listeners.add(fn);
		void syncFeatureFlags();
		return () => { listeners.delete(fn); };
	}, [authToken]);
	return !!cache[name];
}
